import {
  AccordionDetails,
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import {
  FlatAccordion,
  FlatAccordionSummary,
} from 'src/components/FlatAccordion/flat-accordion';
import {
  SidePanelLayoutEntry,
  SidePanelPersonalizationSection,
  SidePanelPropertyKey,
  SidePanelSectionKey,
  usePersonalizedSidePanel,
} from 'src/graphql/entities/side-panel-personalization';
import { ProfileMenu } from './profile-menu';
import { SaveProfileDialog } from './save-profile-dialog';
import { SortablePropertyItem, SortablePropertyList } from './sortable-property-list';

interface PersonalizeSidePanelModalProps {
  open: boolean;
  onClose: () => void;
}

const SECTION_APPEARANCE: Record<
  SidePanelSectionKey,
  { icon: string; getColor: (theme: Theme) => string }
> = {
  conversation: { icon: 'forum', getColor: (theme) => theme.palette.info.main },
  site: { icon: 'home', getColor: (theme) => theme.palette.secondary.main },
  client: { icon: 'person', getColor: (theme) => theme.palette.warning.main },
};

const SectionBlock = ({
  section,
  properties,
  expanded,
  onToggle,
  disabled,
  reorderDisabled,
  onToggleVisible,
  onReorder,
}: {
  section: SidePanelPersonalizationSection;
  properties: SidePanelPersonalizationSection['properties'];
  expanded: boolean;
  onToggle: () => void;
  disabled: boolean;
  reorderDisabled: boolean;
  onToggleVisible: (key: SidePanelPropertyKey) => void;
  onReorder: (nextKeys: SidePanelPropertyKey[]) => void;
}) => {
  const items = useMemo<SortablePropertyItem[]>(
    () =>
      properties.map(({ descriptor, visible }) => ({
        key: descriptor.key,
        label: descriptor.label,
        icon: descriptor.icon,
        group: descriptor.group,
        visible,
      })),
    [properties],
  );

  const shownCount = section.properties.filter((property) => property.visible).length;
  const { icon, getColor } = SECTION_APPEARANCE[section.key];

  return (
    <FlatAccordion expanded={expanded} onChange={onToggle} disableGutters>
      <FlatAccordionSummary expandIcon={<DynamicIcon name="expand_more" />}>
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
          sx={{ flexGrow: 1, minWidth: 0, py: 1 }}
        >
          <Box
            sx={(theme) => ({
              width: '2rem',
              height: '2rem',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: getColor(theme),
              backgroundColor: alpha(getColor(theme), 0.12),
            })}
          >
            <DynamicIcon name={icon} sx={{ fontSize: '1.25rem' }} />
          </Box>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }} noWrap>
            {section.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, pr: 0.5 }}>
            {shownCount} of {section.properties.length} shown
          </Typography>
        </Stack>
      </FlatAccordionSummary>
      <AccordionDetails sx={{ p: 0, pb: 1 }}>
        <SortablePropertyList
          items={items}
          disabled={disabled}
          reorderDisabled={reorderDisabled}
          onToggleVisible={onToggleVisible}
          onReorder={onReorder}
        />
      </AccordionDetails>
    </FlatAccordion>
  );
};

export const PersonalizeSidePanelModal = ({
  open,
  onClose,
}: PersonalizeSidePanelModalProps) => {
  const {
    viewModel: {
      sections,
      effectiveLayout,
      hideEmpty,
      isDraft,
      loading,
      activeProfile,
      profiles,
      actions,
    },
  } = usePersonalizedSidePanel();

  // the first section starts expanded, the rest collapsed
  const [expandedSections, setExpandedSections] = useState<
    Partial<Record<SidePanelSectionKey, boolean>>
  >({});

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // while the view model loads, the effective layout is a builtin-default
  // fallback — an edit would persist it and clobber the stored profile selection
  const editsDisabled = loading || sections.some((section) => section.loading);

  const search = searchTerm.trim().toLowerCase();
  const searching = search !== '';

  // card-rendered surfaces have no draggable rows in any section, so the
  // "drag to reorder" hint would be misleading
  const canReorder = useMemo(
    () => sections.some((section) => !section.reorderDisabled),
    [sections],
  );

  const sectionsToRender = useMemo(
    () =>
      sections
        .map((section) => ({
          section,
          properties: searching
            ? section.properties.filter(({ descriptor }) =>
                descriptor.label.toLowerCase().includes(search),
              )
            : section.properties,
        }))
        .filter(({ properties }) => !searching || properties.length > 0),
    [sections, searching, search],
  );

  const updateSection = useCallback(
    (
      sectionKey: SidePanelSectionKey,
      mapProperties: (properties: SidePanelLayoutEntry[]) => SidePanelLayoutEntry[],
    ) =>
      actions.updateDraft({
        ...effectiveLayout,
        sections: {
          ...effectiveLayout.sections,
          [sectionKey]: {
            properties: mapProperties(effectiveLayout.sections[sectionKey]?.properties ?? []),
          },
        },
      }),
    [actions, effectiveLayout],
  );

  const toggleVisible = useCallback(
    (sectionKey: SidePanelSectionKey, propertyKey: SidePanelPropertyKey) =>
      updateSection(sectionKey, (properties) =>
        properties.map((property) =>
          property.key === propertyKey
            ? { ...property, visible: !property.visible }
            : property,
        ),
      ),
    [updateSection],
  );

  const reorderSection = useCallback(
    (sectionKey: SidePanelSectionKey, nextKeys: SidePanelPropertyKey[]) =>
      updateSection(sectionKey, (properties) => {
        const byKey = new Map(properties.map((property) => [property.key, property]));
        return nextKeys.flatMap((key) => byKey.get(key) ?? []);
      }),
    [updateSection],
  );

  const toggleHideEmpty = useCallback(
    () => actions.updateDraft({ ...effectiveLayout, hideEmpty: !hideEmpty }),
    [actions, effectiveLayout, hideEmpty],
  );

  const ownProfileNames = useMemo(
    () => profiles.filter((profile) => profile.source === 'user').map((p) => p.name),
    [profiles],
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={700}>
            Personalize this view
          </Typography>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <ProfileMenu
              profiles={profiles}
              activeProfileId={activeProfile?.id ?? null}
              isDraft={isDraft}
              disabled={editsDisabled}
              onSelect={actions.applyProfile}
              onDelete={actions.deleteProfile}
              onSaveAs={() => setSaveDialogOpen(true)}
            />
            <IconButton size="small" onClick={onClose} aria-label="Close personalize">
              <DynamicIcon name="close" sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack gap={1}>
          <TextField
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            size="small"
            fullWidth
            placeholder="Search properties"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DynamicIcon name="search" sx={{ fontSize: '1.1rem' }} />
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2">Hide empty fields</Typography>
            <Switch
              size="small"
              checked={hideEmpty}
              disabled={editsDisabled}
              onChange={toggleHideEmpty}
              inputProps={{ 'aria-label': 'Hide empty fields' }}
            />
          </Stack>

          {canReorder ? (
            <Typography variant="caption" color="text.secondary">
              Drag to reorder · click the eye to show or hide.
            </Typography>
          ) : null}

          <Divider />
          <Stack divider={<Divider />}>
            {sectionsToRender.map(({ section, properties }) => {
              // the first section is expanded by default; keyed on the section
              // itself so a filtered search can't shift which one that is
              const defaultExpanded = section.key === sections[0]?.key;

              return (
                <SectionBlock
                  key={section.key}
                  section={section}
                  properties={properties}
                  expanded={searching || (expandedSections[section.key] ?? defaultExpanded)}
                  onToggle={() =>
                    setExpandedSections((current) => ({
                      ...current,
                      [section.key]: !(current[section.key] ?? defaultExpanded),
                    }))
                  }
                  disabled={editsDisabled}
                  reorderDisabled={searching || Boolean(section.reorderDisabled)}
                  onToggleVisible={(propertyKey) => toggleVisible(section.key, propertyKey)}
                  onReorder={(nextKeys) => reorderSection(section.key, nextKeys)}
                />
              );
            })}
          </Stack>
          {searching && sectionsToRender.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No matching properties.
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-start', px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose} sx={{ borderRadius: '999px', px: 3 }}>
          Done
        </Button>
        {isDraft ? (
          <Button size="small" onClick={actions.resetToProfile}>
            Reset
          </Button>
        ) : null}
      </DialogActions>

      <SaveProfileDialog
        open={saveDialogOpen}
        existingNames={ownProfileNames}
        onSave={actions.saveAsProfile}
        onClose={() => setSaveDialogOpen(false)}
      />
    </Dialog>
  );
};
